import React, { createContext } from 'react'
import { BaseContextState, BaseContextProvider } from '../BaseContext'
import IGroup from '../../interfaces/IGroup'
import { SearchRequest, GroupSaveRequest, GroupDeleteRequest } from '../../services/api/interfaces/ApiRequest'
import { groupList, groupCreate, groupUpdate, groupDelete } from '../../services/api'

interface GroupContextState extends BaseContextState {
  groups: Array<IGroup> | null,
  group: IGroup | null
}

interface GroupContextData extends GroupContextState {
  resetErrors: Function,
  requestGroupList: (args: SearchRequest) => void,
  requestGroupFind: (args: SearchRequest) => void,
  requestGroupCreate: (args: GroupSaveRequest) => Promise<any>,
  requestGroupUpdate: (args: GroupSaveRequest) => Promise<any>,
  requestGroupDelete: (args: GroupDeleteRequest) => Promise<any>
}

// Create context
const { Provider, Consumer } = createContext({} as GroupContextData)

// Export consumer
export const GroupConsumer = Consumer

export class GroupProvider extends BaseContextProvider {
  state: GroupContextState = {
    groups: null,
    group: null
  }

  lastListPayload: SearchRequest | null = null

  /**
   * Make api request for group list
   * 
   * @param {SearchRequest} payload 
   */
  requestGroupList = (payload: SearchRequest) => {
    this.setState({
      fetching: true,
      error: null
    }, async () => {
      this.lastListPayload = payload
      const response = await groupList(payload)

      // Process our response
      this.processResponse(response, { 
        paginate: {
          destination: 'groups'
        },
      })
    })
  }

  /**
   * Make api request for group finder
   * 
   * @param {SearchRequest} payload 
   */
  requestGroupFind = (payload: SearchRequest) => {
    this.setState({
      fetching: true,
      error: null,
      group: null
    }, async () => {
      payload.options = {
        ...payload.options,
        _single: true
      }
      const response = await groupList(payload)

      // Process our response
      this.processResponse(response, { 
        mappings: {
          destination: 'group'
        }
      })
    })
  }

  /**
   * Make api request for group create
   * 
   * @param {GroupSaveRequest} payload 
   */
  requestGroupCreate = (payload: GroupSaveRequest) => {
    return new Promise((resolve, reject) => {
      this.setState({
        fetching: true,
        error: null
      }, async () => {
        let response = await groupCreate(payload)
        this.processResponse(response, {
          mappings: [
            { origin: 'group', destination: 'group' }
          ]
        })
  
        if (this.state.success) {
          resolve(true)
        } else {
          reject(this.state.error)
        }
      })
    })
  }

  /**
   * Make api request for group update
   * 
   * @param {GroupSaveRequest} payload 
   */
  requestGroupUpdate = (payload: GroupSaveRequest) => {
    return new Promise((resolve, reject) => {
      this.setState({
        fetching: true,
        error: null
      }, async () => {
        let response = await groupUpdate(payload)
        this.processResponse(response, {
          mappings: [
            { origin: 'group', destination: 'group' }
          ]
        })
  
        if (this.state.success) {
          resolve(true)
        } else {
          reject(this.state.error)
        }
      })
    })
  }

  /**
   * Make api request for group delete
   * 
   * @param {GroupDeleteRequest} payload 
   */
  requestGroupDelete = (payload: GroupDeleteRequest) => {
    return new Promise((resolve, reject) => {
      this.setState({
        fetching: true,
        error: null
      }, async () => {
        let response = await groupDelete(payload)
        this.processResponse(response)
  
        if (this.state.success) {
          this.requestGroupList(this.lastListPayload || {})
          resolve(true)
        } else {
          reject(this.state.error)
        }
      })
    })
  }

  render() {
    return (
      <Provider
        value={{
          // States
          ...this.state,

          // Dispatches
          resetErrors: this.resetErrors,
          requestGroupList: this.requestGroupList,
          requestGroupFind: this.requestGroupFind,
          requestGroupCreate: this.requestGroupCreate,
          requestGroupUpdate: this.requestGroupUpdate,
          requestGroupDelete: this.requestGroupDelete
        }}
      >
        {this.props.children}
      </Provider>
    )
  }
}
